import { makeAutoObservable, runInAction } from 'mobx';
import { $api } from '../http';
import { jwtDecode } from 'jwt-decode';
import { IUser } from '../types/entities';
import CartStore from './CartStore';

type RegistrationResult =
    | {
        success: true;
        message: string;
      }
    | {
        success: false;
        error: string;
        code: string | null;
    };

export default class UserStore {
    // State
    _isAuth = false;
    _user: IUser | null = null;
    _isLoading = !!localStorage.getItem("token");     
    _cartStore: CartStore | null = null;
    _error: string | null = null;
    _errorCode: string | null = null;

    isLoginModalOpen = false;
    isForgotPasswordModalOpen = false;

    isRegistrationSuccessModalOpen = false;
    registeredEmail = "";
    initialLoginEmail = "";

    constructor() {
        makeAutoObservable(this);
    }

    // Actions
    setIsAuth(bool: boolean) {
        this._isAuth = bool;
    }

    setUser(user: IUser | null) {
        // EN: Add a warning to help debug if the user object from the token is incomplete.
        // PL: Dodaj ostrzeżenie, aby pomóc w debugowaniu, jeśli obiekt użytkownika z tokenu jest niekompletny.
        if (user && !user.firstName) {
            console.warn("User object from token is missing 'firstName'. Check backend JWT generation.", user);
        }
        this._user = user;
    }

    setCartStore(cartStore: CartStore) {
        this._cartStore = cartStore;
    }

    setLoading(bool: boolean) {
        this._isLoading = bool;
    }

    setError(error: string | null, code: string | null = null) {
        this._error = error;
        this._errorCode = code;
    }

    // EN: Update openLoginModal to accept optional email argument
    openLoginModal = (email: string = "") => {
        this.initialLoginEmail = email;
        this.isLoginModalOpen = true;
        this.setError(null); // Clear previous errors when opening
    };

    closeLoginModal = () => {
        this.isLoginModalOpen = false;
        this.initialLoginEmail = "";
    }

    // EN: Actions for the "Forgot Password" modal.
    // PL: Akcje dla modalu "Nie pamiętasz hasła?".
    openForgotPasswordModal = () => {
        this.isForgotPasswordModalOpen = true;
        this.setError(null);
    };

    closeForgotPasswordModal = () => {
        this.isForgotPasswordModalOpen = false;
    }

    openRegistrationSuccessModal = (email: string) => {
        this.registeredEmail = email;
        this.isRegistrationSuccessModalOpen = true;
    };

    closeRegistrationSuccessModal = () => {
        this.isRegistrationSuccessModalOpen = false;
        this.registeredEmail = "";
    };

    // Getters
    get isAuth() {
        return this._isAuth;
    }

    get user() {
        return this._user;
    }

    get error() {
        return this._error;
    }

    get errorCode() {
        return this._errorCode;
    }

    get isLoading() {
        return this._isLoading;
    }

    // Async Actions (Thunks)
    async login(email: string, password: string) {
        this.setLoading(true);
        this.setError(null);
        try {
            const response = await $api.post<{ token: string }>('/auth/login', { email, password });
            
            // EN: Set token immediately so subsequent requests (like cart merge) are authenticated.
            // PL: Ustaw token natychmiast, aby kolejne żądania (np. scalanie koszyka) były uwierzytelnione.
            localStorage.setItem('token', response.data.token);

            // EN: After successful login, merge the guest cart with the user's cart.
            // PL: Po pomyślnym zalogowaniu, połącz koszyk gościa z koszykiem użytkownika.
            if (this._cartStore) {
                await this._cartStore.mergeAndFetchCart();
            }

            runInAction(() => {
                const userData: IUser = jwtDecode(response.data.token);
                this.setIsAuth(true);
                this.setUser(userData);
                this.closeLoginModal();
            });
        } catch (e: any) {
            localStorage.removeItem('token'); // Clean up bad token on failed login
            // EN: Log the full error for better debugging, especially for 403 errors which can be tricky.
            // PL: Zaloguj pełny błąd w celu lepszego debugowania, szczególnie w przypadku trudnych do zdiagnozowania błędów 403.
            console.error('Login request failed:', e.response || e);
            const errorMessage = e.response?.data?.message || 'Failed to log in. Please check your details or try again later.';
            runInAction(() => this.setError(errorMessage));
            // EN: Do not re-throw. The component will react to the error state. This prevents unhandled promise rejections in tests.
            // PL: Nie rzucaj ponownie. Komponent zareaguje na stan błędu. Zapobiega to nieobsłużonym odrzuceniom obietnic w testach.
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    async registration(
        email: string, 
        password: string, 
        firstName: string, 
        phone: string, 
        lastName: string | undefined, 
        recaptchaToken: string
    ): Promise<RegistrationResult> {
        this.setLoading(true);
        this.setError(null);

        try {
            const response = await $api.post<{ 
                token: string;
                message: string;
            }>('/auth/registration', 
                {
                    email, password, firstName, phone, lastName, recaptchaToken
                }
            );

            runInAction(() => {
                // EN: If you want user to log in immediately upon registration, keep these.
                // If you require email verification before login, remove setIsAuth/setUser here.
                localStorage.setItem('token', response.data.token);
                const userData: IUser = jwtDecode(response.data.token);
                this.setUser(userData);
                this.setIsAuth(true);

                // EN: Trigger global modal open
                this.openRegistrationSuccessModal(email);
            });

            return { success: true, message: response.data.message };
        } catch (e: any) {
            const errorMessage = e.response?.data?.message || 'Błąd podczas rejestracji.';
            // EN: Fallback to HTTP status if backend does not provide a explicit custom error string code
            // PL: Użyj statusu HTTP 409 jako kodu, jeśli backend nie zwraca jawnego kodu ciągu znaków
            const httpStatus = e.response?.status;
            const errorCode = e.response?.data?.code || (httpStatus === 409 ? 'ACCOUNT_ALREADY_EXISTS' : null);

            runInAction(() => {
                this.setError(errorMessage, errorCode);
            });

            return { success: false, error: errorMessage, code: errorCode };
        } finally {
            runInAction(() => { 
                this.setLoading(false);
            });
        }
    }

    async forgotPassword(email: string) {
        this.setLoading(true);
        this.setError(null);
        try {
            // We don't need to handle the response, just the status
            await $api.post('/auth/forgot-password', { email });
        } catch (e: any) {
            // For security, we don't reveal if the email exists.
            // The error is thrown to be caught by the component, but we don't set a global error state.
            console.error('Forgot password request failed', e.response?.data?.message);
            // EN: Do not re-throw. The component will react to the loading/success state.
            // PL: Nie rzucaj ponownie. Komponent zareaguje na stan ładowania/sukcesu.
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    async resetPassword(token: string, password: string) {
        this.setLoading(true);
        this.setError(null);
        try {
            const response = await $api.post('/auth/reset-password', { token, password });
            // EN: After a successful password reset, the backend returns a new token. Let's log the user in.
            // PL: Po pomyślnym zresetowaniu hasła backend zwraca nowy token. Zalogujmy użytkownika.
            runInAction(() => {
                localStorage.setItem('token', response.data.token);
                const userData: IUser = jwtDecode(response.data.token);
                this.setIsAuth(true);
                this.setUser(userData);
            });

            return { success: true };

        } catch (e: any) {
            const errorMessage = e.response?.data?.message || 'Reset password failed';
            runInAction(() => this.setError(errorMessage));
            
            return { success: false, error: errorMessage };
            
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }

    logout() {
        localStorage.removeItem('token');
        this.setIsAuth(false);
        this.setUser(null);
        this.setError(null);
        this.closeLoginModal(); // Ensure modal is closed on logout

        // EN: After logging out, clear the current (authenticated) cart state
        //     and attempt to load a guest cart if one exists in localStorage.
        // PL: Po wylogowaniu wyczyść bieżący stan koszyka (uwierzytelnionego)
        //     i spróbuj załadować koszyk gościa, jeśli istnieje w localStorage.
        if (this._cartStore) {
            this._cartStore.setCart(null);
            this._cartStore.loadGuestCart();
        }
    }

    async checkAuth() {
        this.setLoading(true);
        try {
            const response = await $api.get<{ token: string }>('/auth/check');
            localStorage.setItem('token', response.data.token); // Refresh token

            // EN: Fetch the authenticated user's cart.
            // PL: Pobierz koszyk uwierzytelnionego użytkownika.
            if (this._cartStore) {
                await this._cartStore.fetchCart();
            }

            const userData: IUser = jwtDecode(response.data.token);
            runInAction(() => {
                this.setIsAuth(true);
                this.setUser(userData);
            });
        } catch (e) {
            // EN: This is an expected path for guest users or users with an expired token.
            // We call logout() to clear any stale auth state and initialize the guest cart.
            // PL: To jest oczekiwana ścieżka dla gości lub użytkowników z wygasłym tokenem.
            // Wywołujemy logout(), aby wyczyścić nieaktualny stan uwierzytelnienia i zainicjować koszyk gościa.
            console.info("Auth check failed, initializing as guest.");
            this.logout();
        } finally {
            runInAction(() => this.setLoading(false));
        }
    }
}